import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReleaseNotesService } from '../../services/release-notes.service';
import { MatCardModule } from '@angular/material/card';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTabsModule } from '@angular/material/tabs';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import {
  ReleaseNote,
  ReleaseNoteCriticality,
  ReleaseNoteItem,
} from '@pulpo/pulpo-models';

@Component({
  selector: 'app-release-notes-page',
  templateUrl: './release-notes-page.component.html',
  styleUrls: ['./release-notes-page.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatListModule,
    MatIconModule,
    MatDividerModule,
    MatExpansionModule,
    MatTabsModule,
    MatBadgeModule,
    MatButtonModule,
    MatChipsModule,
    MatTooltipModule,
    MatSnackBarModule,
  ],
})
export class ReleaseNotesPageComponent implements OnInit {
  releaseNotes: ReleaseNote[] = [];
  selectedVersion: ReleaseNote | null = null;
  originalVersionItems: ReleaseNoteItem[] | undefined = [];
  selectedVersionIndex = 0;
  allExpanded = false;
  panelOpenState: boolean[] = [];

  // Criticality filter state
  criticalityFilters: { [key in ReleaseNoteCriticality]: boolean } = {
    high: true,
    medium: true,
    technical: true,
    trivial: true,
  };

  constructor(
    private releaseNotesService: ReleaseNotesService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadReleaseNotes();

    // Modify the event listener to not interfere with buttons
    document.addEventListener('click', (event) => {
      // Check if the click is not on a button
      const target = event.target as HTMLElement;
      const isButton =
        target.closest('button') ||
        target.closest('.criticality-btn') ||
        target.closest('.collapse-all-btn');

      // Ignore clicks on buttons
      if (isButton) {
        return;
      }

      // Refresh the selection only for clicks outside buttons
      if (
        this.selectedVersionIndex !== null &&
        this.selectedVersionIndex >= 0
      ) {
        setTimeout(() => {
          this.selectVersion(this.selectedVersionIndex);
        }, 10);
      }
    });
  }

  loadReleaseNotes() {
    this.releaseNotesService.getReleaseNotes().subscribe({
      next: (notes) => {
        // Sort versions by date (from newest to oldest)
        this.releaseNotes = notes.sort((a, b) => {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          return dateB - dateA;
        });

        // Automatically select the first version
        if (this.releaseNotes.length > 0) {
          this.selectVersion(0);
        }
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading release notes:', error);
        this.cdr.markForCheck();
      },
    });
  }

  selectVersion(index: number) {
    this.selectedVersionIndex = index;
    this.selectedVersion = this.releaseNotes[index];

    // Reset filters
    this.resetCriticalityFilters();

    // Sort items by criticality (high > medium > technical > trivial)
    if (this.selectedVersion && this.selectedVersion.items) {
      // Create a copy to avoid modifying the original directly
      const sortedItems = [...this.selectedVersion.items];

      // Define the criticality order for sorting
      const criticalityOrder: { [key in ReleaseNoteCriticality]: number } = {
        high: 1,
        medium: 2,
        technical: 3,
        trivial: 4,
      };

      // Sort items by criticality
      sortedItems.sort((a, b) => {
        const critA = a.criticality || 'trivial';
        const critB = b.criticality || 'trivial';
        return criticalityOrder[critA] - criticalityOrder[critB];
      });

      // Save the original sorted items
      this.originalVersionItems = [...sortedItems];

      // Replace items with the sorted version
      this.selectedVersion = {
        ...this.selectedVersion,
        items: sortedItems,
      };
    }

    // Initialize panel state for this version based on criticality
    if (this.selectedVersion && this.selectedVersion.items) {
      this.panelOpenState = this.selectedVersion.items.map(
        (item: ReleaseNoteItem) => item.criticality !== 'trivial' // Trivial items are collapsed by default
      );
    }
  }

  toggleAllPanels() {
    this.allExpanded = !this.allExpanded;

    // Update the state of all panels
    if (this.selectedVersion && this.selectedVersion.items) {
      this.panelOpenState = new Array(this.selectedVersion.items.length).fill(
        this.allExpanded
      );
    }
  }

  setPanelState(index: number, isOpen: boolean) {
    if (this.panelOpenState) {
      this.panelOpenState[index] = isOpen;

      // Check if all panels are open or closed
      const allOpen = this.panelOpenState.every((state) => state === true);
      const allClosed = this.panelOpenState.every((state) => state === false);

      if (allOpen !== this.allExpanded && allOpen) {
        this.allExpanded = true;
      } else if (allClosed !== !this.allExpanded && allClosed) {
        this.allExpanded = false;
      }
    }
  }

  // Function to get the badge color based on criticality
  getCriticalityColor(criticality: ReleaseNoteCriticality | undefined): string {
    if (!criticality) return 'default';

    switch (criticality) {
      case 'high':
        return 'warn';
      case 'medium':
        return 'primary';
      case 'technical':
        return 'accent';
      case 'trivial':
        return 'default';
      default:
        return 'default';
    }
  }

  // Function to get the icon based on criticality
  getCriticalityIcon(criticality: ReleaseNoteCriticality | undefined): string {
    if (!criticality) return 'info';

    switch (criticality) {
      case 'high':
        return 'priority_high';
      case 'medium':
        return 'arrow_upward';
      case 'technical':
        return 'code';
      case 'trivial':
        return 'low_priority';
      default:
        return 'info';
    }
  }

  // Function to display criticality information on button click
  showCriticalityInfo(criticality: ReleaseNoteCriticality): void {
    let title: string;
    let description: string;
    let panelClass: string;

    switch (criticality) {
      case 'high':
        title = 'Critical';
        description =
          'Major fixes or essential features that directly affect users.';
        panelClass = 'high-snackbar';
        break;
      case 'medium':
        title = 'Important';
        description =
          'Significant improvements or fixes that enhance the user experience.';
        panelClass = 'medium-snackbar';
        break;
      case 'technical':
        title = 'Technical';
        description =
          "Technical or architectural changes that don't directly affect the user experience.";
        panelClass = 'technical-snackbar';
        break;
      case 'trivial':
        title = 'Minor';
        description = 'Small improvements or low-impact fixes on daily usage.';
        panelClass = 'trivial-snackbar';
        break;
      default:
        title = 'Information';
        description = 'Details about changes made to the application.';
        panelClass = 'default-snackbar';
    }

    this.snackBar.open(`${title}: ${description}`, 'Close', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: [panelClass],
    });
  }

  // Function to enable/disable a criticality filter
  toggleCriticalityFilter(criticality: ReleaseNoteCriticality): void {
    // Toggle filter state
    this.criticalityFilters[criticality] =
      !this.criticalityFilters[criticality];

    // Apply filters
    this.applyFilters();
  }

  // Function to reset all filters
  resetCriticalityFilters(): void {
    this.criticalityFilters = {
      high: true,
      medium: true,
      technical: true,
      trivial: true,
    };
  }

  // Function to apply criticality filters
  applyFilters(): void {
    if (!this.selectedVersion || !this.originalVersionItems) return;

    // Filter items based on active filters
    const filteredItems = this.originalVersionItems.filter((item) => {
      const criticality = item.criticality || 'trivial';
      return this.criticalityFilters[criticality];
    });

    // Update selected version with filtered items
    this.selectedVersion = {
      ...this.selectedVersion,
      items: filteredItems,
    };

    // Update panel states
    if (this.selectedVersion && this.selectedVersion.items) {
      this.panelOpenState = this.selectedVersion.items.map(
        (item: ReleaseNoteItem) =>
          item.criticality !== 'trivial' || this.allExpanded
      );
    }
  }

  // Function to check if a filter is active
  isFilterActive(criticality: ReleaseNoteCriticality): boolean {
    return this.criticalityFilters[criticality];
  }
}
